import React from 'react'
import { Box, Container, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { blue, grey } from '../../colors'
import { InstructorAlert, InstructorAttributeAlert, SectionWrapper } from '../SurveyFeature/styles'
import { indentationColors } from '../SurveyTempleteFeature/constant'
import AttributesFilterChips from '../../components/AttributesFilterChips'
import { type ISurveySection } from '../../models/SurveyTemplate'
import { showQuestion } from './SurveyPreviewUtils'
import { BranchingOperation } from '../SurveyTempleteFeature/types'

interface WithIndentationViewProps {
  surveySections: ISurveySection[]
  isSurveyProjectAddedSection?: boolean
}

const showBranchingDetails = (section: ISurveySection): JSX.Element => {
  const { prompt, operation, answer } = section.branchingFilters ?? {}

  const formatAnswer = (answer: string[] = []): string =>
    answer?.map((_answer: string, index: number) => `"${_answer}"${index !== answer?.length - 1 ? ', ' : ''}`).join(' ')

  const formattedAnswer = formatAnswer(answer)

  return (
    <Typography component="span" display="inline" fontWeight={500}>
      This survey section only shows if, {' '}
      <Typography component="span" display="inline" fontWeight={600}>
        &quot;{prompt}&quot; {operation !== 'is' && 'is'} {operation} {operation === BranchingOperation.Is && formattedAnswer}
      </Typography>
    </Typography>
  )
}

const SinglePageView: React.FC<WithIndentationViewProps> = ({ surveySections }) => {
  const getDisplayTitle = (section: ISurveySection): string => {
    if (section.isSurveyProjectAddedSection && section.instructorName) {
      return `Instructor Added Questions by ${section.instructorName}`
    }
    const displayTitle = section?.title?.trim()
    return displayTitle || ''
  }

  const getSubtitle = (section: ISurveySection): string => {
    const description = section?.descr?.trim()
    return description || ''
  }

  return (
    <>
      {surveySections?.map((section, index) => (
        <Container key={section.surveySectionId} sx={{ paddingTop: 0, paddingBottom: 12 }}>
          <Box width="100%" display="flex" gap="14px" pl={`${section.indentation * 30}px`}>
            {Boolean(section.indentation) && (
              <Box
                borderRadius="8px"
                width="12px"
                minHeight="100%"
                bgcolor={indentationColors[section.indentation] ?? indentationColors[5]}
              ></Box>
            )}
            <Box width="100%">
              <Tooltip title={'This banner will not show on the actual survey.'} placement="top" arrow>
                <Box mt={1}>
                  {section.branchingFilters?.itemId && (
                    <InstructorAttributeAlert severity="info" icon={<InfoOutlinedIcon sx={{ color: grey[500] }} />}>
                      {showBranchingDetails(section)}
                    </InstructorAttributeAlert>
                  )}
                </Box>
              </Tooltip>
              <Box>
                {section.instructorName && (
                  <InstructorAlert severity="info" icon={<InfoOutlinedIcon sx={{ color: blue[500] }} />}>
                    <Typography component="span" display="inline" fontWeight={500}>The following section should be answered for {' '}</Typography>
                    <Typography component="span" display="inline" fontWeight={600}>
                      {section.instructorName}
                    </Typography>.
                  </InstructorAlert>
                )}
              </Box>
              <Box sx={{ marginTop: 1 }}>
                {section?.isSurveyProjectAddedSection === true && (
                  <InstructorAttributeAlert severity="info" icon={<InfoOutlinedIcon sx={{ color: grey[950], fontWeight: 600 }} />}>
                    {section.instructorName
                      ? <>This survey section contains questions added by instructor <strong>{section.instructorName}</strong>.</>
                      : 'This survey section contains questions added by the instructor.'}
                  </InstructorAttributeAlert>
                )}
              </Box>
              <SectionWrapper>
                <Typography variant="h5" fontWeight={600} fontSize="20px" color={grey[250]}>
                  {getDisplayTitle(section)}
                </Typography>
                {getSubtitle(section) && (
                  <Typography fontSize={'13px'} fontWeight={400} color={blue[1100]}>
                    {getSubtitle(section)}
                  </Typography>
                )}
                <Box marginTop={2}>
                  <AttributesFilterChips attributes={section.attributes ?? []} />
                </Box>
                {showQuestion(section)}
              </SectionWrapper>
            </Box>
          </Box>
        </Container>
      ))}
    </>
  )
}

export default SinglePageView
